import { Services } from '@/services/Services';
import { PolicyErrorCodesEnum } from '@general/js/enums';
import { getCurrentDate, DHLError, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils/';
import { axiosRequest, handleHttpResponse } from '@general/js/utils/RequestAPIUtils';
import { EnvironmentVariables } from '@general/js/environmentVariables';

export default class RebateServices {
  static async retrieveRebateHistory(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_REBATE_HISTORY(memberNumber, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`retrieveRebateHistory() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getRebateLandingRefData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_REBATE_LANDING_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getRebateLandingRefData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getRebateTierHistoryRefData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_REBATE_HISTORY_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getRebateTierHistoryRefData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getAddRebateRefData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_ADD_REBATE_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getAddRebateRefData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async submitRebateRegistration(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.REBATES.POST_ADD_REBATE(memberNumber), payload, {
        timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT),
      });

      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`submitRebateRegistration() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`submitRebateRegistration() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`submitRebateRegistration() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`submitRebateRegistration() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async retrieveMembershipDetails(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.COMMON.GET_RETRIEVE_MEMBERSHIP_DETAILS(memberNumber, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`retrieveMembershipDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getUpdateRebateRefData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_UPDATE_REBATE_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getUpdateRebateRefData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async submitUpdatedRebateTier(memberNumber, payload) {
    try {
      const response = await axiosRequest().patch(Services.MY_COVER.REBATES.PATCH_UPDATE_REBATE(memberNumber), payload, {
        timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT),
      });

      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`submitUpdatedRebateTier() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`submitUpdatedRebateTier() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`submitUpdatedRebateTier() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`submitUpdatedRebateTier() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getWithdrawRebateRefData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.REBATES.GET_WITHDRAW_REBATE_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getWithdrawRebateRefData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async submitRebateWithdrawal(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.REBATES.POST_WITHDRAW_REBATE(memberNumber), payload, {
        timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT),
      });

      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`submitRebateWithdrawal() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`submitRebateWithdrawal() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`submitRebateWithdrawal() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`submitRebateWithdrawal() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async estimateNewPremium(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.REBATES.POST_ESTIMATE_NEW_REBATE_PREMIUM(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`estimateNewPremium() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
